import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useState } from "react";
import { POST } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import { Eye, EyeOff } from "lucide-react";
import { CopyIcon, DownloadIcon } from "../../components/icons";
import EntityConstants from "../../configs/EntityConstants";
import warningIcon from "../../resources/icons/warning-triangle.svg";
import downloadCSV from "../../service/downloadCSV";
import { useParams } from "react-router-dom";
import AuthContext from "../../store/auth-context";
import { useTranslation } from "react-i18next";
import { emailRegex } from "../../configs/App.Regex";

export default function CreateTenantUserModal({
  isOpen,
  onClose,
  onUserCreated,
}) {
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [successData, setSuccessData] = useState(null);
  const { tenant_code } = useParams();

  const maxCharsName = 50;
  const maxCharsEmail = 100;

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setShowPassword(false);
    setErrors({
      firstName: "",
      lastName: "",
      email: "",
    });
    setErrorMessage("");
    setSuccessData(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
    };

    if (!firstName.trim()) {
      newErrors.firstName = t("tenant:first_name_required");
      valid = false;
    } else if (firstName.length > maxCharsName) {
      newErrors.firstName = `${t(
        "tenant:first_name_must_be",
      )} ${maxCharsName} ${t("tenant:characters_or_less")}`;
      valid = false;
    }

    if (!lastName.trim()) {
      newErrors.lastName = t("tenant:last_name_required");
      valid = false;
    } else if (lastName.length > maxCharsName) {
      newErrors.lastName = `${t(
        "tenant:last_name_must_be",
      )} ${maxCharsName} ${t("tenant:characters_or_less")}`;
      valid = false;
    }

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      newErrors.email = t("tenant:email_required");
      valid = false;
    } else if (!emailRegex.test(trimmedEmail)) {
      newErrors.email = t("tenant:valid_email_address");
      valid = false;
    } else if (trimmedEmail.length > maxCharsEmail) {
      newErrors.email = `${t("tenant:email_must_be")} ${maxCharsEmail} ${t(
        "tenant:characters_or_less",
      )}`;
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    if (!authContext.userType) {
      Toast({
        type: "error",
        title: t("tenant:error"),
        message: t("tenant:user_type_not_loaded"),
      });
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    setSuccessData(null);

    const payload = {
      email: email.trim(),
      tenant_code: tenant_code,
      user_type:
        authContext.userType === "super_admin" ? "Tenant_Admin" : "Tenant_User",
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    };

    POST(EntityConstants.POST_CREATE_USERS, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          Toast({
            type: "success",
            title: t("tenant:success"),
            message: data.message || t("tenant:user_created_successfully"),
          });
          setSuccessData(data.data);
          onUserCreated();
        } else {
          setErrorMessage(data.message || t("tenant:failed_create_user"));
          Toast({
            type: "error",
            title: t("tenant:error"),
            message: data.message || t("tenant:failed_create_user"),
          });
        }
      })
      .catch((error) => {
        console.error("Create user error:", error);
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: t("tenant:failed_create_user_retry"),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        Toast({
          type: "success",
          title: t("tenant:copied"),
          message: t("tenant:copied_clipboard"),
        });
      })
      .catch(() => {
        Toast({
          type: "error",
          title: t("tenant:error"),
          message: t("tenant:failed_copy_clipboard"),
        });
      });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id="create-tenant-user-modal"
                className="max-w-xl transform overflow-hidden rounded-2xl bg-cfSecondary p-6 text-left align-middle shadow-xl transition-all w-full relative"
              >
                <form
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <button
                    type="button"
                    id="create-tenant-user-modal-close"
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>

                  <div className="">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-cfHeadingText"
                    >
                      {successData
                        ? t("tenant:user_created_successfully_title")
                        : t("tenant:create_new_user")}
                    </Dialog.Title>
                    <p className="text-sm text-cfGlobalText mt-1">
                      {successData
                        ? t("tenant:user_details_created_save_password")
                        : `${t(
                            "tenant:create_new_user_under_tenant",
                          )} ${tenant_code}`}
                    </p>

                    {!successData ? (
                      <div className="space-y-4 mt-6">
                        <div className="flex flex-col w-full gap-2">
                          <div className="w-full">
                            <label
                              htmlFor="create-tenant-user-modal-input-first-name"
                              className="block mb-2 text-sm font-medium text-cfHeadingText"
                            >
                              {t("tenant:first_name")}{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="create-tenant-user-modal-input-first-name"
                              className={`bg-cfTextInputBorder border ${
                                errors.firstName
                                  ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                                  : "border-cfIssuedScreenSearchBorder"
                              } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                              placeholder="e.g. John"
                              value={firstName}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= maxCharsName) {
                                  setFirstName(value);
                                  setErrors({ ...errors, firstName: "" });
                                }
                              }}
                            />
                            <div className="flex justify-between items-center mt-1">
                              {errors.firstName && (
                                <p
                                  id="create-tenant-user-modal-error-first-name"
                                  className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                                >
                                  {errors.firstName}
                                </p>
                              )}
                              <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                                <span>
                                  {maxCharsName - firstName.length}{" "}
                                  {t("tenant:characters_left")}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="w-full">
                            <label
                              htmlFor="create-tenant-user-modal-input-last-name"
                              className="block mb-2 text-sm font-medium text-cfHeadingText"
                            >
                              {t("tenant:last_name")}{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="create-tenant-user-modal-input-last-name"
                              className={`bg-cfTextInputBorder border ${
                                errors.lastName
                                  ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                                  : "border-cfIssuedScreenSearchBorder"
                              } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                              placeholder="e.g. Doe"
                              value={lastName}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value.length <= maxCharsName) {
                                  setLastName(value);
                                  setErrors({ ...errors, lastName: "" });
                                }
                              }}
                            />
                            <div className="flex justify-between items-center mt-1">
                              {errors.lastName && (
                                <p
                                  id="create-tenant-user-modal-error-last-name"
                                  className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                                >
                                  {errors.lastName}
                                </p>
                              )}
                              <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                                <span>
                                  {maxCharsName - lastName.length}{" "}
                                  {t("tenant:characters_left")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="create-tenant-user-modal-input-email"
                            className="block mb-2 text-sm font-medium text-cfHeadingText"
                          >
                            {t("tenant:email")}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="email"
                            id="create-tenant-user-modal-input-email"
                            className={`bg-cfTextInputBorder border ${
                              errors.email
                                ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                                : "border-cfIssuedScreenSearchBorder"
                            } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                            placeholder="e.g. john.doe@example.com"
                            value={email}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= maxCharsEmail) {
                                setEmail(value);
                                setErrors({ ...errors, email: "" });
                              }
                            }}
                          />
                          <div className="flex justify-between items-center mt-1">
                            {errors.email && (
                              <p
                                id="create-tenant-user-modal-error-email"
                                className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG"
                              >
                                {errors.email}
                              </p>
                            )}
                            <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                              <span>
                                {maxCharsEmail - email.length}{" "}
                                {t("tenant:characters_left")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4 mt-6">
                        <div className="p-4 bg-green-50 border-none border-green-200 rounded-lg">
                          <p className="text-green-800 text-sm">
                            {t("tenant:user_created_temp_password_sent")}
                          </p>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label
                              htmlFor="create-tenant-user-modal-success-name"
                              className="block mb-1 text-sm font-medium text-cfHeadingText"
                            >
                              {t("tenant:name")}
                            </label>
                            <div className="flex items-center">
                              <input
                                type="text"
                                id="create-tenant-user-modal-success-name"
                                readOnly
                                className="bg-cfTextInputBorder border border-cfIssuedScreenSearchBorder text-cfGlobalText text-sm rounded-lg p-2.5 w-full"
                                value={`${successData.first_name} ${successData.last_name}`}
                              />
                              <button
                                type="button"
                                id="create-tenant-user-modal-icon-copy-name"
                                onClick={() =>
                                  copyToClipboard(
                                    `${successData.first_name} ${successData.last_name}`,
                                  )
                                }
                                className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                aria-label={t("tenant:copy_name")}
                              >
                                <CopyIcon size={18} className="inline-flex" aria-hidden />
                              </button>
                            </div>
                          </div>

                          <div>
                            <label
                              htmlFor="create-tenant-user-modal-success-email"
                              className="block mb-1 text-sm font-medium text-cfHeadingText"
                            >
                              {t("tenant:email")}
                            </label>
                            <div className="flex items-center">
                              <input
                                type="email"
                                id="create-tenant-user-modal-success-email"
                                readOnly
                                className="bg-cfTextInputBorder border border-cfIssuedScreenSearchBorder text-cfGlobalText text-sm rounded-lg p-2.5 w-full"
                                value={successData.email}
                              />
                              <button
                                type="button"
                                id="create-tenant-user-modal-icon-copy-email"
                                onClick={() =>
                                  copyToClipboard(successData.email)
                                }
                                className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                aria-label={t("tenant:copy_email")}
                              >
                                <CopyIcon size={18} className="inline-flex" aria-hidden />
                              </button>
                            </div>
                          </div>

                          <div>
                            <label
                              htmlFor="create-tenant-user-modal-success-password"
                              className="block mb-1 text-sm font-medium text-cfHeadingText"
                            >
                              {t("tenant:temporary_password")}
                            </label>
                            <div className="flex items-center">
                              <div className="relative flex-grow">
                                <input
                                  type={showPassword ? "text" : "password"}
                                  id="create-tenant-user-modal-success-password"
                                  readOnly
                                  className="bg-cfTextInputBorder border border-cfIssuedScreenSearchBorder text-cfGlobalText text-sm rounded-lg p-2.5 w-full pr-10"
                                  value={successData.temp_password}
                                />
                                <button
                                  type="button"
                                  id="create-tenant-user-modal-icon-toggle-password"
                                  onClick={() => setShowPassword(!showPassword)}
                                  className="absolute focus:outline-none right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                  {showPassword ? (
                                    <EyeOff size={18} />
                                  ) : (
                                    <Eye size={18} />
                                  )}
                                </button>
                              </div>
                              <button
                                type="button"
                                id="create-tenant-user-modal-icon-copy-password"
                                onClick={() =>
                                  copyToClipboard(successData.temp_password)
                                }
                                className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                aria-label={t("tenant:copy_temporary_password")}
                              >
                                <CopyIcon size={18} className="inline-flex" aria-hidden />
                              </button>
                              <button
                                type="button"
                                id="create-tenant-user-modal-icon-download-credentials"
                                onClick={() =>
                                  downloadCSV(
                                    {
                                      email: successData.email,
                                      temporary_password:
                                        successData.temp_password,
                                    },
                                    `${t("tenant:tenant_admin_credentials")}.csv`,
                                  )
                                }
                                className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                aria-label={t("tenant:download_credentials")}
                              >
                                <DownloadIcon size={21} className="inline-flex" aria-hidden />
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg">
                          <div>
                            <img
                              src={warningIcon}
                              className="h-10 w-10"
                              alt=""
                            />
                          </div>
                          <div className="text-red-800 text-sm">
                            {t("tenant:save_details_security_note")}
                          </div>
                        </div>
                      </div>
                    )}

                    {errorMessage && (
                      <p
                        id="create-tenant-user-modal-error-message"
                        className="mt-4 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG flex items-center"
                      >
                        <span className="mr-1 text-base leading-none">*</span>
                        {errorMessage}
                      </p>
                    )}
                  </div>

                  <div className="flex mt-8 justify-end gap-4">
                    {!successData ? (
                      <>
                        <button
                          type="button"
                          id="create-tenant-user-modal-cancel"
                          className="px-4 py-2 text-cfHeadingText border hover:bg-cfCardHover transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                          onClick={handleClose}
                          disabled={isLoading}
                        >
                          {t("tenant:cancel")}
                        </button>
                        <button
                          type="submit"
                          id="create-tenant-user-modal-submit"
                          className="px-4 py-2 bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2 flex items-center justify-center min-w-[120px]"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span className="flex items-center">
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              {t("tenant:creating")}
                            </span>
                          ) : (
                            t("tenant:create_user")
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        id="create-tenant-user-modal-done"
                        className="px-4 py-2 bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                        onClick={handleClose}
                      >
                        {t("tenant:done")}
                      </button>
                    )}
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
